import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getSpecialOfferApiUrl } from '../../config/api';
import './SpecialOfferSection.css';

const SpecialOfferSection = ({ specialOfferData }) => {
    const { i18n } = useTranslation();
    const [offerData, setOfferData] = useState(null);
    const [couponsLeft, setCouponsLeft] = useState(3);
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [isDetailsVisible, setIsDetailsVisible] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        service: 'landing-page'
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null
    const [submitMessage, setSubmitMessage] = useState(''); // Сообщение от сервера
    const [timeLeft, setTimeLeft] = useState(0); // Время в секундах
    const hasLoadedData = useRef(false); // Флаг для отслеживания загрузки данных

    // Обновляем данные когда приходят новые пропсы
    useEffect(() => {
        if (specialOfferData) {
            console.log('🎯 SpecialOfferSection: Received new data from props:', specialOfferData);
            console.log('🎯 SpecialOfferSection: Setting couponsLeft to:', specialOfferData.coupons_left);
            setOfferData(specialOfferData);
            setCouponsLeft(specialOfferData.coupons_left || 0);

            if (specialOfferData.time_left_seconds > 0) {
                setTimeLeft(specialOfferData.time_left_seconds);
                localStorage.setItem('special_offer_time_left', specialOfferData.time_left_seconds.toString());
                localStorage.setItem('special_offer_timestamp', Date.now().toString());
            }

            hasLoadedData.current = true;
        }
    }, [specialOfferData]);

    // Загружаем данные с сервера для глобального таймера (fallback)
    useEffect(() => {
        // Если данные уже загружены или пришли через пропсы, не загружаем повторно
        if (hasLoadedData.current || specialOfferData) {
            console.log('🎯 SpecialOfferSection: Skipping data fetch - already loaded or received via props');
            return;
        }

        const fetchOfferData = async () => {
            try {
                const apiUrl = getSpecialOfferApiUrl('special-offer');
                const response = await fetch(`${apiUrl}?t=${Date.now()}`);
                const data = await response.json();
                if (data.status === 'success' && data.offer) {
                    setOfferData(data.offer);
                    setCouponsLeft(data.offer.coupons_left);

                    // Всегда используем данные с сервера для точности
                    // localStorage может быть устаревшим после изменений в админ панели
                    if (data.offer.time_left_seconds > 0) {
                        setTimeLeft(data.offer.time_left_seconds);
                        localStorage.setItem('special_offer_time_left', data.offer.time_left_seconds.toString());
                        localStorage.setItem('special_offer_timestamp', Date.now().toString());
                    }

                    hasLoadedData.current = true; // Отмечаем, что данные загружены
                }
            } catch (error) {
                console.error('Error fetching offer data:', error);
            }
        };

        fetchOfferData();
    }, [specialOfferData]); // Пустой массив зависимостей - загружаем только один раз

    // Таймер обратного отсчета
    useEffect(() => {
        if (timeLeft > 0) {
            const id = setInterval(() => {
                setTimeLeft(prevTime => {
                    const newTime = prevTime <= 1 ? 0 : prevTime - 1;

                    // Сохраняем время в localStorage
                    if (newTime > 0) {
                        localStorage.setItem('special_offer_time_left', newTime.toString());
                        localStorage.setItem('special_offer_timestamp', Date.now().toString());
                    } else {
                        // Время истекло, очищаем localStorage
                        localStorage.removeItem('special_offer_time_left');
                        localStorage.removeItem('special_offer_timestamp');
                    }

                    return newTime;
                });
            }, 1000);

            return () => {
                clearInterval(id);
            };
        }
    }, [timeLeft]); // Запускаем когда timeLeft изменяется

    // Если блок неактивен или нет данных, не показываем его
    if (!offerData || !offerData.is_active) {
        return null;
    }

    const formatTime = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);
        setSubmitMessage('');

        // Валидация на фронтенде
        if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) {
            setSubmitStatus('error');
            setIsSubmitting(false);
            return;
        }

        // Проверка email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setSubmitStatus('error');
            setIsSubmitting(false);
            return;
        }

        try {
            const apiUrl = getSpecialOfferApiUrl('special-offer');
            const requestData = {
                ...formData,
                language_code: i18n.language
            };

            console.log('🔗 Using API URL:', apiUrl);
            console.log('📤 Sending data:', requestData);

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestData)
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Error response:', errorText);
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            if (result.success) {
                setSubmitStatus('success');
                setSubmitMessage(result.message || ''); // Сохраняем сообщение от сервера
                setCouponsLeft(result.data?.coupons_left || 0);
                setFormData({ name: '', email: '', phone: '', service: 'landing-page' });

                // Перезагружаем данные с сервера для обновления счетчика
                const refreshResponse = await fetch(`${apiUrl}?t=${Date.now()}`);
                const refreshData = await refreshResponse.json();
                if (refreshData.status === 'success' && refreshData.offer) {
                    setCouponsLeft(refreshData.offer.coupons_left);
                    setOfferData(refreshData.offer);
                }

                setTimeout(() => {
                    setIsFormVisible(false);
                    setSubmitStatus(null);
                    setSubmitMessage('');
                }, 5000);
            } else {
                setSubmitStatus('error');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };


    if (!offerData) {
        return null; // Скрываем блок, если данные не загружены
    }

    if (!offerData.is_active) {
        return null; // Скрываем блок, если он не активен
    }



    const discountPrice = (offerData.original_price * (100 - offerData.discount_percent) / 100).toFixed(2);
    const savings = (offerData.original_price - discountPrice).toFixed(0);

    // Если данные еще не загружены, показываем загрузку
    if (!offerData) {
        return (
            <section className="special-offer-section">
                <div className="container">
                    <div className="offer-card">
                        <div className="offer-content">
                            <h2>Загрузка...</h2>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="special-offer-section">
            <div className="container">
                <div className="offer-card">
                    <div className="offer-header">
                        <div className="offer-badge">
                            <span className="badge-text">{offerData.translations?.[i18n.language]?.badge || offerData.translations?.lt?.badge || 'RIBOTA PASIŪLYMAS'}</span>
                        </div>
                        <div className="coupons-left">
                            <span className="coupons-count">{couponsLeft}</span>
                            <span className="coupons-text">{offerData.translations?.[i18n.language]?.couponsLeft || offerData.translations?.lt?.couponsLeft || 'kuponų liko'}</span>
                        </div>
                    </div>

                    <div className="offer-content">
                        <h2 className="offer-title">
                            {offerData.translations?.[i18n.language]?.title || offerData.translations?.lt?.title || 'Gaukite iki 30% nuolaidą svetainės kūrimui!'}
                        </h2>

                        <div className="offer-timer">
                            <span className="timer-label">{offerData.translations?.[i18n.language]?.timerLabel || offerData.translations?.lt?.timerLabel || 'Iki akcijos pabaigos:'}</span>
                            <span className="timer-value">{formatTime(timeLeft)}</span>
                        </div>

                        <div className="offer-details">
                            <h3>{offerData.translations?.[i18n.language]?.serviceTitle || offerData.translations?.lt?.serviceTitle || 'Kas įeina į paslaugą:'}</h3>
                            <ul className="offer-features">
                                {(offerData.translations?.[i18n.language]?.features || offerData.translations?.lt?.features || 'Puslapio struktūros parengimas ir prototipas\nModernus, patogus naudoti dizainas (veikiantis telefone ir kompiuteryje)\nSvetainės sukūrimas su naujausiomis technologijomis (Next.js + TailwindCSS)\nKontaktų forma užklausoms gauti\nPagrindinis SEO sutvarkymas (kad svetainę rastų Google)\nGoogle Analytics prijungimas (lankytojams sekti)\nSvetainės įkėlimas į serverį ir paleidimas\n1 mėnesio techninė pagalba')
                                    .split('\n')
                                    .filter(feature => feature.trim())
                                    .map((feature, index) => (
                                        <li key={index}>{feature.trim()}</li>
                                    ))
                                }
                            </ul>
                        </div>

                        <div className="offer-price">
                            <span className="original-price">{offerData.original_price}€</span>
                            <span className="discount-price">{discountPrice}€</span>
                            <span className="savings">Sutaupykite {savings}€!</span>
                        </div>

                        <button
                            className="details-toggle-btn"
                            onClick={() => setIsDetailsVisible(!isDetailsVisible)}
                        >
                            {isDetailsVisible
                                ? (offerData.translations?.[i18n.language]?.hideDetails || offerData.translations?.lt?.hideDetails || '↑ Slėpti detales')
                                : (offerData.translations?.[i18n.language]?.showDetails || offerData.translations?.lt?.showDetails || '↓ Išsamiau apie akciją')
                            }
                        </button>

                        {isDetailsVisible && (
                            <div className="offer-details-block">
                                <div className="details-content">
                                    <h3>{offerData.translations?.[i18n.language]?.detailsTitle || offerData.translations?.lt?.detailsTitle || 'Išsamiai apie akciją'}</h3>
                                    <div className="details-text">
                                        {(offerData.translations?.[i18n.language]?.detailsText || offerData.translations?.lt?.detailsText || 'Sukurkite modernų lėdingo puslapį su Next.js ir TailwindCSS. Pilnas paketas "po raktą" - nuo idėjos iki paleidimo!')
                                            .split('\n')
                                            .filter(line => line.trim())
                                            .map((line, index) => (
                                                <p key={index} dangerouslySetInnerHTML={{
                                                    __html: line
                                                        .replace(/📌/g, '<span class="icon">📌</span>')
                                                        .replace(/⏱/g, '<span class="icon">⏱</span>')
                                                        .replace(/🛠/g, '<span class="icon">🛠</span>')
                                                        .replace(/💡/g, '<span class="icon">💡</span>')
                                                        .replace(/•/g, '&bull;')
                                                }} />
                                            ))
                                        }
                                    </div>

                                    <div className="conditions-block">
                                        <h4>{offerData.translations?.[i18n.language]?.conditionsTitle || offerData.translations?.lt?.conditionsTitle || 'Akcijos sąlygos:'}</h4>
                                        <div className="conditions-text">
                                            {(offerData.translations?.[i18n.language]?.conditions || offerData.translations?.lt?.conditions || 'Nuolaida: 25% nuo bazinio paketo "Lėdingo puslapis po raktą" kainos\nBazinė kaina: 600€ → su kuponu: 450€\nGaliojimo laikas: iki 2025 m. rugsėjo 30 d. imtinai\nPasiūlymų skaičius: tik 3 vietos (išsekus akcija užsidaro)\nKuponas: unikalus kodas bus išsiųstas el. paštu po užklausos')
                                                .split('\n')
                                                .filter(condition => condition.trim())
                                                .map((condition, index) => (
                                                    <p key={index}>• {condition.trim()}</p>
                                                ))
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {!isFormVisible ? (
                            <button
                                className="get-coupon-btn"
                                onClick={() => setIsFormVisible(true)}
                            >
                                {offerData.translations?.[i18n.language]?.buttonText || offerData.translations?.lt?.buttonText || 'Gauti nuolaidą'}
                            </button>
                        ) : (
                            <div className="coupon-form-container">
                                <form onSubmit={handleSubmit} className="coupon-form">
                                    <h3>{offerData.translations?.[i18n.language]?.formTitle || offerData.translations?.lt?.formTitle || 'Užpildykite formą nuolaidai gauti'}</h3>

                                    <div className="form-group">
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            placeholder={offerData.translations?.[i18n.language]?.namePlaceholder || offerData.translations?.lt?.namePlaceholder || 'Jūsų vardas'}
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            placeholder={offerData.translations?.[i18n.language]?.emailPlaceholder || offerData.translations?.lt?.emailPlaceholder || 'El. pašto adresas'}
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            placeholder={offerData.translations?.[i18n.language]?.phonePlaceholder || offerData.translations?.lt?.phonePlaceholder || 'Telefono numeris'}
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <select
                                            name="service"
                                            value={formData.service}
                                            onChange={handleInputChange}
                                        >
                                            <option value="landing-page">{offerData.translations?.[i18n.language]?.landingPage || offerData.translations?.lt?.landingPage || 'Tinklapis'}</option>
                                            <option value="website">{offerData.translations?.[i18n.language]?.website || offerData.translations?.lt?.website || 'Svetainė'}</option>
                                            <option value="ecommerce">{offerData.translations?.[i18n.language]?.ecommerce || offerData.translations?.lt?.ecommerce || 'El. parduotuvė'}</option>
                                            <option value="kita">{offerData.translations?.[i18n.language]?.kita || offerData.translations?.lt?.kita || 'Kita'}</option>
                                        </select>
                                    </div>

                                    <button
                                        type="submit"
                                        className="submit-btn"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (offerData.translations?.[i18n.language]?.submitting || offerData.translations?.lt?.submitting || 'Siunčiama...') : (offerData.translations?.[i18n.language]?.submit || offerData.translations?.lt?.submit || 'Gauti nuolaidą')}
                                    </button>

                                    {submitStatus === 'success' && (
                                        <div className="success-message">
                                            {submitMessage || (() => {
                                                const currentLang = i18n.language || 'lt';
                                                const successMsg = offerData.translations?.[currentLang]?.successMessage ||
                                                    offerData.translations?.lt?.successMessage ||
                                                    'Ačiū! Susisieksime su jumis artimiausiu metu.';
                                                return successMsg.replace('{{email}}', formData.email);
                                            })()}
                                        </div>
                                    )}

                                    {submitStatus === 'error' && (
                                        <div className="error-message">
                                            {offerData.translations?.[i18n.language]?.errorMessage || offerData.translations?.lt?.errorMessage || 'Įvyko klaida. Bandykite dar kartą.'}
                                        </div>
                                    )}
                                </form>

                                <button
                                    className="close-form-btn"
                                    onClick={() => setIsFormVisible(false)}
                                >
                                    {offerData.translations?.[i18n.language]?.close || offerData.translations?.lt?.close || 'Uždaryti'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default SpecialOfferSection;